import { useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Input, Modal, Select, useToast } from '../../../design-system';
import '../../styles/Renewals.css';

// Add or correct a tracked renewal. Editing the expiry date here is a
// CORRECTION; recording an actual new certificate is the Renew action, which
// preserves the old period.
const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'renewed', label: 'Renewed' },
  { value: 'cancelled', label: 'Cancelled / surrendered' },
];

function RenewalEditModal({ record = {}, types = [], onClose, onSaved }) {
  const toast = useToast();
  const isNew = !record.id;
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    clientId: record.clientId || '',
    type: record.type || 'Trade_License',
    customType: record.customType || '',
    number: record.number || '',
    issuingAuthority: record.issuingAuthority || '',
    validFrom: record.validFrom ? String(record.validFrom).slice(0, 10) : '',
    validTo: record.validTo ? String(record.validTo).slice(0, 10) : '',
    status: record.status || 'active',
    leadDays: record.leadDays ?? '',
    dscClass: record.dscClass || '',
    usageType: record.usageType || '',
    notes: record.notes || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) return;
    apiClient.get('/api/clients/options')
      .then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || [])))
      .catch(() => setClients([]));
  }, [isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const chosen = types.find((t) => t.code === form.type);

  const save = async () => {
    if (isNew && !form.clientId) { toast.danger('Choose a client'); return; }
    if (!form.validTo) { toast.danger('An expiry date is required'); return; }
    if (form.type === 'Other' && !form.customType.trim()) { toast.danger('Name the type'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        leadDays: form.leadDays === '' ? null : Number(form.leadDays),
        validFrom: form.validFrom || undefined,
      };
      if (isNew) await apiClient.post('/api/expirables', payload);
      else await apiClient.put(`/api/expirables/${record.id}`, payload);
      toast.success(isNew ? 'Renewal tracked' : 'Saved');
      onSaved?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen onClose={onClose}
      title={isNew ? 'Track a renewal' : `Edit — ${record.typeLabel}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving}>Save</Button>
        </>
      }
    >
      <div className="rnw-form">
        {isNew && (
          <Select label="Client" value={form.clientId} onChange={(e) => set('clientId', e.target.value)}>
            <option value="">— choose —</option>
            {clients.map((c) => (
              <option key={c._id || c.id} value={c._id || c.id}>
                {c.displayName || c.name}{c.clientCode ? ` (${c.clientCode})` : ''}
              </option>
            ))}
          </Select>
        )}

        <div className="rnw-form-row">
          <Select label="What expires" value={form.type} onChange={(e) => set('type', e.target.value)}>
            {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </Select>
          {form.type === 'Other' && (
            <Input label="Name it" value={form.customType} onChange={(e) => set('customType', e.target.value)} />
          )}
          <Input label="Number" value={form.number} onChange={(e) => set('number', e.target.value)} />
        </div>

        <div className="rnw-form-row">
          <Input label="Valid from" type="date" value={form.validFrom} onChange={(e) => set('validFrom', e.target.value)} />
          <Input label="Expires on" type="date" value={form.validTo} onChange={(e) => set('validTo', e.target.value)} />
          <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)}>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
        </div>

        <div className="rnw-form-row">
          <Input label="Issuing authority" value={form.issuingAuthority} onChange={(e) => set('issuingAuthority', e.target.value)} />
          <Input
            label={`Remind this many days ahead${chosen ? ` (default ${chosen.leadDays})` : ''}`}
            type="number" min="0" max="365" placeholder={chosen ? String(chosen.leadDays) : ''}
            value={form.leadDays} onChange={(e) => set('leadDays', e.target.value)}
          />
        </div>

        {form.type === 'DSC' && (
          <div className="rnw-form-row">
            <Input label="DSC class" value={form.dscClass} onChange={(e) => set('dscClass', e.target.value)} />
            <Input label="Usage (MCA / GST / DGFT…)" value={form.usageType} onChange={(e) => set('usageType', e.target.value)} />
          </div>
        )}

        <div className="ds-field">
          <label className="ds-field-label" htmlFor="rnw-notes">Notes</label>
          <textarea id="rnw-notes" className="ds-input" rows="2" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>

        <p className="rnw-sub">
          Changing the expiry here is a <b>correction</b>. When a new certificate is actually issued,
          use <b>Renew</b> instead — that keeps the old period on record.
        </p>
      </div>
    </Modal>
  );
}

export default RenewalEditModal;
